import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';
import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';
import { useTranslate } from 'src/locales';
import { TenantType } from 'src/types/enums';
import { CompanyProfileType, ICompanyListItem } from 'src/services/connections/connections.types';
import { showCompanyEditor } from 'src/store/slices/companies/companiesSlice';
import { openTaskEditorSidesheet } from 'src/store/slices/app/appSlice';
import { useAppDispatch } from 'src/store/store';

import Iconify from 'src/components/iconify';
import CustomPopover, { usePopover } from 'src/components/custom-popover';

// ----------------------------------------------------------------------

/**
 * Company row kebab for the shared DataList (JT1-1528). Ports the old companies-table-row actions:
 * View (detail route needs tenant + company type), Edit (unlinked profiles only, opens the editor),
 * and Add Task. Reads the flat ICompanyListItem.
 */
export default function CompanyRowActions({ row }: { row: ICompanyListItem }) {
  const { t } = useTranslate();
  const router = useRouter();
  const dispatch = useAppDispatch();
  const popover = usePopover();
  const tenantType = useOrgTenant();

  const view = () => router.push(paths.dashboard.companies.detail(row.id, tenantType as TenantType, (row.company_type ?? tenantType) as TenantType));

  return (
    <>
      <IconButton color={popover.open ? 'inherit' : 'default'} onClick={popover.onOpen}>
        <Iconify icon="eva:more-horizontal-fill" />
      </IconButton>

      <CustomPopover open={popover.open} onClose={popover.onClose} arrow="right-top" sx={{ width: 200 }}>
        <MenuItem onClick={() => { popover.onClose(); view(); }}>
          <Iconify icon="solar:eye-bold" />
          <Typography variant="body2">{t('common.view')}</Typography>
        </MenuItem>

        {row.type === CompanyProfileType.UNLINKED && (
          <MenuItem onClick={() => { popover.onClose(); dispatch(showCompanyEditor(row.id)); }}>
            <Iconify icon="material-symbols:edit" />
            <Typography variant="body2">{t('common.edit')}</Typography>
          </MenuItem>
        )}

        <MenuItem onClick={() => { popover.onClose(); dispatch(openTaskEditorSidesheet({ companyId: row.id })); }}>
          <Iconify icon="ic:round-add-task" />
          <Typography variant="body2">{t('common.add_task')}</Typography>
        </MenuItem>
      </CustomPopover>
    </>
  );
}
